//src/utils/opaque-registration.utils.ts

import { RegistrationResponse, type OpaqueClient } from '@cloudflare/opaque-ts';

import { OPAQUE_CONFIG, SERVER_IDENTITY } from '@/lib/opaque-config';

import {
  bytesToHex,
  hexToBytes,
  unwrapResult,
  createOpaqueClient,
  EMPTY_LENGTH,
  toUint8Array,
} from './opaque-utils';
import { isSerializable } from './type-guards';

/**
 * Result of OPAQUE registration initialization
 */
export interface OpaqueRegistrationInit {
  registrationRequest: string;
  client: OpaqueClient;
}

/**
 * Result of OPAQUE registration finalization
 */
export interface OpaqueRegistrationFinish {
  sessionId: string;
  registrationRecord: string;
}

/**
 * Creates an OPAQUE registration request for the server
 *
 * This is Step 1 of the OPAQUE registration flow:
 * 1. Client generates a registration request from the password
 * 2. Request is sent to server for OPRF evaluation
 *
 * @param password - User's password (will be cleared from memory after use)
 * @returns Registration request as hex string and client instance (must be reused)
 * @throws Error if registration request creation fails
 */
export async function createRegistrationRequest(password: string): Promise<OpaqueRegistrationInit> {
  const client = createOpaqueClient();

  // Generate registration request
  const requestResult = await client.registerInit(password);
  const request = unwrapResult(requestResult, 'Failed to create registration request');

  // Validate request has serialize method
  if (typeof request.serialize !== 'function') {
    throw new Error('Invalid registration request: missing serialize method');
  }

  // Serialize to bytes
  const serialized = request.serialize();
  const requestBytes = toUint8Array(serialized);

  if (requestBytes.length === EMPTY_LENGTH) {
    throw new Error('Registration request serialization returned empty bytes');
  }

  return {
    registrationRequest: bytesToHex(requestBytes),
    client, // Return client for reuse in finalizeRegistrationRecord
  };
}

/**
 * Finalizes OPAQUE registration by creating the encrypted registration record
 *
 * This is Step 2 of the OPAQUE registration flow:
 * 1. Receives server's registration response (OPRF evaluation)
 * 2. Creates encrypted registration record with server identity
 * 3. Returns record to send to server for storage
 *
 * @param client - The OPAQUE client instance from createRegistrationRequest (must be reused)
 * @param registrationResponseHex - Server's registration response (hex string)
 * @param email - User's email (used as client identity)
 * @returns Encrypted registration record as hex string
 * @throws Error if registration finalization fails
 */
export async function finalizeRegistrationRecord(
  client: OpaqueClient,
  registrationResponseHex: string,
  email: string
): Promise<string> {
  // Deserialize server's registration response
  const responseBytes = hexToBytes(registrationResponseHex);
  const response = RegistrationResponse.deserialize(OPAQUE_CONFIG, responseBytes);

  // Create encrypted registration record
  const finishResult = await client.registerFinish(response, SERVER_IDENTITY, email);
  const unwrappedResult = unwrapResult(finishResult, 'Failed to finalize registration');

  // Extract record (it might be wrapped in a property)
  const record = extractRecord(unwrappedResult);

  // Validate and serialize record
  if (!isSerializable(record)) {
    throw new Error('Invalid registration record: missing serialize method');
  }

  return bytesToHex(record.serialize());
}

/**
 * Complete OPAQUE registration flow helper
 *
 * Combines both steps of OPAQUE registration:
 * 1. Creates registration request
 * 2. Processes server response and creates final record
 *
 * Note: This function is NOT used directly because the client instance
 * must persist between createRegistrationRequest and finalizeRegistrationRecord calls.
 * Use createRegistrationRequest and finalizeRegistrationRecord separately instead.
 *
 * @param client - The OPAQUE client instance from createRegistrationRequest
 * @param serverResponse - Server's registration response from Step 1
 * @param email - User's email (client identity)
 * @returns Final registration record to send to server
 * @throws Error if any step of registration fails
 */
export async function performOpaqueRegistration(
  client: OpaqueClient,
  serverResponse: { sessionId: string; registrationResponse: string },
  email: string
): Promise<{ sessionId: string; registrationRecord: string }> {
  const { sessionId, registrationResponse } = serverResponse;

  // Validate server response
  if (
    sessionId.length === EMPTY_LENGTH ||
    typeof registrationResponse !== 'string' ||
    registrationResponse.length === EMPTY_LENGTH
  ) {
    throw new Error('Invalid server response: missing required fields');
  }

  // Finalize registration with the same client instance
  const registrationRecord = await finalizeRegistrationRecord(client, registrationResponse, email);

  return {
    sessionId,
    registrationRecord,
  };
}

/* ─── Private Helpers ─── */

/**
 * Extracts registration record from finish result
 * (handles both wrapped and unwrapped formats)
 */
function extractRecord(unwrappedResult: unknown): unknown {
  const hasRecordProperty = (obj: unknown): obj is { record: unknown } =>
    typeof obj === 'object' && obj !== null && 'record' in obj;

  return hasRecordProperty(unwrappedResult) ? unwrappedResult.record : unwrappedResult;
}
